import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError } from '../lib/api';
import { useSession, useAsync } from '../lib/session';
import { inr, date, today } from '../lib/fmt';
import type { ClaimRow } from '../lib/types';
import {
  Button, Card, Field, Input, Select, Modal, Note, PageHead, Pill, Tag,
  Table, Th, Td, TableWrap, Empty, Tile, Tiles,
} from '../components/ui';
import { Loading, TilesSkeleton, TableSkeleton } from '../components/skeleton';

interface Feed {
  claims: ClaimRow[];
  stats: { submitted: number; submitted_value: number; approved: number; approved_value: number; flagged: number } | null;
  pending_for_me: number; escalated: number; my_limit: number | null;
}

export default function Claims() {
  const { user, can, meta } = useSession();
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [pay, setPay] = useState<ClaimRow | null>(null);
  const [reject, setReject] = useState<ClaimRow | null>(null);
  const [err, setErr] = useState('');

  const feed = useAsync<Feed>(() => api.get('/api/claims', { status, q }), [status, q]);

  async function approve(c: ClaimRow) {
    setErr('');
    try { await api.post(`/api/claims/${c._id}/approve`, {}); feed.reload(); }
    catch (e) { setErr(e instanceof ApiError ? e.message : String(e)); }
  }

  const d = feed.data;

  return (
    <div>
      <PageHead
        eyebrow={can('expense:read_all') ? 'All claims' : 'Your claims'}
        title="Expenses"
        dek={can('expense:approve')
          ? <>Approving is a second pair of eyes — you cannot clear your own, and anything above{' '}
             <b>{d?.my_limit === null ? 'no limit' : inr(d?.my_limit ?? 0)}</b> goes up a level.</>
          : 'Everything you have submitted, and where it has got to.'}
        actions={<Link to="/capture"
          className="inline-flex items-center rounded bg-brand text-brand-ink font-semibold
                     text-[14px] md:text-[13px] px-3.5 py-2.5 min-h-[44px] md:min-h-0 no-underline hover:brightness-95">Add an expense</Link>}
      />

      {err && <Note title="That did not work" tone="critical">{err}</Note>}

      {feed.loading ? <TilesSkeleton /> : d?.stats && (
        <Tiles>
          <Tile hero label="Waiting on approval" value={String(d.stats.submitted)}
                sub={<span className="text-muted">{inr(d.stats.submitted_value)} in value</span>} />
          <Tile label="Approved, not yet paid" value={inr(d.stats.approved_value)}
                sub={<span className="text-muted">{d.stats.approved} claim{d.stats.approved === 1 ? '' : 's'}</span>} />
          <Tile label="Flagged for a closer look" value={String(d.stats.flagged)}
                sub={<span className="text-muted">unclear reading, or large</span>} />
          <Tile label="Your ceiling" value={d.my_limit === null ? 'No limit' : inr(d.my_limit)}
                sub={<span className="text-muted">{d.escalated ? `${d.escalated} above it` : 'nothing above it'}</span>} />
        </Tiles>
      )}

      <div className="flex flex-wrap items-end gap-2.5 mb-4">
        <Field label="Search"><Input value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Vendor, description, claim no…" className="min-w-[220px]" /></Field>
        <Field label="Status">
          <Select value={status} onChange={(e) => setStatus(e.target.value)} className="min-w-[150px]">
            <option value="">Any status</option>
            {['submitted','approved','paid','rejected'].map((s) => <option key={s} value={s}>{s}</option>)}
          </Select>
        </Field>
      </div>

      <Card flush>
        {feed.loading ? <Loading label="Loading claims"><TableSkeleton rows={6} cols={8} /></Loading>
         : !d?.claims.length ? <Empty>No claims match that.</Empty>
         : (
          <TableWrap>
            <Table cards>
              <thead><tr>
                <Th>Claim</Th><Th>Date</Th><Th>Paid to</Th><Th>For</Th><Th>By</Th>
                <Th n>Amount</Th><Th>Status</Th><Th />
              </tr></thead>
              <tbody>
                {d.claims.map((c) => {
                  const mine = String((c.submitted_by as { _id?: string })?._id ?? c.submitted_by) === user?.id;
                  return (
                    <tr key={c._id}>
                      <Td label="" className="font-mono text-[12.5px] font-semibold">
                        {c.claim_no}{c.attachment_count ? <> <Tag>{c.attachment_count} 📎</Tag></> : null}
                      </Td>
                      <Td label="Date" className="font-mono text-[11.5px]">{date(c.spent_on)}</Td>
                      <Td label="Paid to">{c.vendor?.name ?? c.vendor_text ?? '—'}</Td>
                      <Td label="For" className="text-ink-2">
                        {c.description ?? c.category?.name ?? '—'}
                        {!c.has_tax_invoice && <> <Tag>no GST bill</Tag></>}
                        {c.needs_review && <> <Tag tone="brand">check</Tag></>}
                      </Td>
                      <Td label="By">{(c.submitted_by as { name?: string })?.name ?? '—'}</Td>
                      <Td n label="Amount"><b>{inr(c.amount)}</b></Td>
                      <Td label="Status"><Pill kind={c.status}>{c.status}</Pill></Td>
                      <Td label="" className="text-right whitespace-nowrap sm:whitespace-nowrap">
                        {c.status === 'submitted' && can('expense:approve') && !mine && (
                          <>
                            <Button size="sm" onClick={() => void approve(c)}>Approve</Button>{' '}
                            <Button size="sm" variant="ghost" onClick={() => setReject(c)}>Reject</Button>
                          </>
                        )}
                        {c.status === 'approved' && can('expense:pay') && (
                          <Button size="sm" variant="primary" onClick={() => setPay(c)}>Pay</Button>
                        )}
                      </Td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </TableWrap>
        )}
      </Card>

      {reject && <RejectModal claim={reject} onClose={() => setReject(null)}
                              onDone={() => { setReject(null); feed.reload(); }} />}
      {pay && <PayModal claim={pay} accounts={meta?.accounts ?? []} onClose={() => setPay(null)}
                        onDone={() => { setPay(null); feed.reload(); }} />}
    </div>
  );
}

function RejectModal({ claim, onClose, onDone }: { claim: ClaimRow; onClose: () => void; onDone: () => void }) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  return (
    <Modal title={`Reject ${claim.claim_no}`} onClose={onClose} confirmLabel="Reject claim" busy={busy}
      onConfirm={async () => {
        setBusy(true); setErr('');
        try { await api.post(`/api/claims/${claim._id}/reject`, { reason }); onDone(); }
        catch (e) { setErr(e instanceof ApiError ? e.message : String(e)); setBusy(false); }
      }}>
      <p className="text-[13px] text-ink-2 mb-3.5">
        {inr(claim.amount)} to {claim.vendor?.name ?? claim.vendor_text}. The reason is shown to whoever submitted it.
      </p>
      <Field label="Reason">
        <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="What should they fix?" />
      </Field>
      {err && <p className="mt-2 text-[12.5px] text-critical">{err}</p>}
    </Modal>
  );
}

function PayModal(
  { claim, accounts, onClose, onDone }:
  { claim: ClaimRow; accounts: { id: string; name: string }[]; onClose: () => void; onDone: () => void },
) {
  const [account, setAccount] = useState(claim.paid_from?._id ?? accounts[0]?.id ?? '');
  const [when, setWhen] = useState(today());
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  return (
    <Modal title={`Pay ${claim.claim_no}`} onClose={onClose} confirmLabel={`Pay ${inr(claim.amount)}`} busy={busy}
      onConfirm={async () => {
        setBusy(true); setErr('');
        try { await api.post(`/api/claims/${claim._id}/pay`, { paid_from: account, paid_on: when }); onDone(); }
        catch (e) { setErr(e instanceof ApiError ? e.message : String(e)); setBusy(false); }
      }}>
      <p className="text-[13px] text-ink-2 mb-3.5">
        {inr(claim.amount)} to {claim.vendor?.name ?? claim.vendor_text}. This draws the money from the account you pick.
      </p>
      <div className="grid grid-cols-2 gap-3.5">
        <Field label="Paid from">
          <Select value={account} onChange={(e) => setAccount(e.target.value)}>
            {accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </Select>
        </Field>
        <Field label="Paid on"><Input type="date" value={when} onChange={(e) => setWhen(e.target.value)} /></Field>
      </div>
      {err && <p className="mt-2 text-[12.5px] text-critical">{err}</p>}
    </Modal>
  );
}
